import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';

import PrivateRoute from "./routers/PrivateRoute.js";
import Header from "./components/Header/Header.js";
import Home from "./components/MainPages/HomePage.js";
import Login from "./components/MainPages/LoginPage.js";
import ManageUserPage from "./components/UserManagePages/ManageUserPage.js";
import AddUserPage from "./components/UserManagePages/AddUserPage.js";
import AssignRolePage from "./components/UserManagePages/AssignRolePage.js";
import UpdateUserInformationPage from "./components/UserManagePages/UpdateUserInformationPage.js";
import Dashboard from "./components/PerformanceDashboardComponents/Dashboard.js";
import TicketContainer from "./components/TicketComponents/TicketContainer.js";
import ScrollToTop from "./components/Design/ScrollToTop.js";
import NotFound from "./components/CommonPages/NotFound.js"
import ErrorPage from "./components/CommonPages/ErrorPage.js";
import UnauthorizedPage from "./components/CommonPages/Unauthorized.js";
import LoadingIndicator from "./components/CommonPages/LoadingIndicator.js";
import ManageLeavePage from "./components/LeaveHandling/ManageLeavePage.js";
import LeaveRequestPage from "./components/LeaveHandling/LeaveRequest.js";
import HolidayPage from "./components/Holiday/HolidayPage.js";
import ManageAssignments from "./components/ManageAssignment/ManageAssignments.js";
import ServiceFlowRoutes from "./components/ServiceStages/ServiceFlowRoutes.js";
import CompanyDomainsAdminPage from "./components/CompanyDomain/CompanyDomainsAdminPage.js";
import DashboardBO from "./components/PerformanceDashboardComponents/DashboardBO.js";
import UrgentTicketRaiseReport from "./components/PerformanceDashboardComponents/UrgentTicketRaiseReport.js";
import ForumFlowRoute from "./components/ForumPages/ForumFlowRoute.js";
import AssignmentPreviewPage from "./components/ManageAssignment/AssignmentPreviewPage.js";

import OAuth2RedirectHandler from "./oauth2/OAuthRedirectHandler.js";
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './auth/AuthHandler.js';
import { WebSocketProvider } from "./components/WebSocket/WebSocketContext.js";
import { VAPID_KEY, API_BASE_URL } from "./constants/index.js";
import { useApiClient } from "./service/API.js";
import { SidebarProvider } from "./components/Header/SidebarContext.js";
import { useSidebar } from "./components/Header/SidebarContext.js";
import OCRUploadPage from "./components/FileHandling/OCRPage.js";

const App = () => {
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const location = useLocation();
  const { expiration, authenticated, userEmail, userName, userRoles, loading, handleLogout, handleLogin, mute, popup } = useAuth();
  const apiClient = useApiClient();

  // Request notification permission and subscribe
  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        const registration = await navigator.serviceWorker.ready;
        await subscribeToPushNotifications(registration);
      } else {
        console.log('Notification permission denied.');
      }
    } catch (error) {
      console.error('Notification permission request failed:', error);
    }
  };

  const clearUnwantedCacheEntries = async () => {
    const cache = await caches.open('push-subscription-cache');
    const keys = await cache.keys();
  
    for (const request of keys) {
      const url = new URL(request.url);
      const pathname = url.pathname;
  
      if (pathname !== '/user-subscription') {
        console.log('Deleting from cache:', pathname);
        await cache.delete(request);
      }
    }
  };

  const subscribeToPushNotifications = async (registration) => {
    try {
      const existingSubscription = await registration.pushManager.getSubscription();
      let popupstate = popup;
      if (['BOUSER'].some(role => userRoles.includes(role))) {
        popupstate = true;
      }


      if (existingSubscription) {
        console.log('Existing subscription found:', existingSubscription);
        await sendSubscriptionToBackend(existingSubscription); // Update the existing subscription

        try {
          // First check if cache exists
          const cacheStorage = await caches.open('push-subscription-cache');
          const existingCacheResponse = await cacheStorage.match('/user-subscription');

          // Create the new subscription data
          const subscriptionData = {
            email: userEmail,
            subscription: existingSubscription,
            apiURL: API_BASE_URL,
            mute: mute,
            popup: popup
          };

          const subscriptionRequest = new Request('/user-subscription');

          // Update the cache
          await cacheStorage.put(subscriptionRequest, new Response(JSON.stringify(subscriptionData)));

          await clearUnwantedCacheEntries();


          console.log('Cache updated successfully');
        } catch (error) {
          console.error('Error updating subscription cache:', error);
        }

        return;
      }

      // Create a new subscription if none exists
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      console.log('New Subscription:', subscription);
      await sendSubscriptionToBackend(subscription);
      // Save the user's email and subscription in the cache
      caches.open('push-subscription-cache').then((cache) => {
        const subscriptionData = {
          email: userEmail,
          subscription,
          apiURL: API_BASE_URL,
          mute,
          popup: popupstate,
        };

        const req = new Request('/user-subscription');
        return cache.put(req, new Response(JSON.stringify(subscriptionData)));
      });

    } catch (err) {
      console.error('Push subscription failed:', err);
    }
  };

  // Send subscription data to the backend
  const sendSubscriptionToBackend = async (subscription) => {
    const p256dh = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');
  
    if (p256dh && auth) {
      const p256dhBase64 = btoa(String.fromCharCode(...new Uint8Array(p256dh)));
      const authBase64 = btoa(String.fromCharCode(...new Uint8Array(auth)));
  
      const subscriptionData = {
        email: userEmail,
        endpoint: subscription.endpoint,
        p256dh: p256dhBase64,
        auth: authBase64,
      };
  
      try {
        const response = await apiClient.post('/service/subscribe', subscriptionData);
        console.log('Subscription sent to backend successfully');
        return response.data;
      } catch (error) {
        console.error('Failed to send subscription to backend:', error);
        throw error;
      }
    } else {
      console.error('Subscription keys are missing or invalid:', subscription);
      throw new Error('Subscription keys are missing or invalid');
    }
  };

  // Utility to convert base64 string to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    // Check if base64String is defined
    if (!base64String) {
      console.error('Base64 string is undefined or empty');
      throw new Error('Invalid base64 string provided');
    }

    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  useEffect(() => {
    if (authenticated) {
      // Trigger push notification subscription after login
      requestNotificationPermission();
    }
  }, [authenticated]); // Run this effect whenever `authenticated` changes

  useEffect(() => {
  // Are Notifications supported in the service worker?
    if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
      console.warn('Notifications aren\'t supported.');
      return;
    }

    // Check the current Notification permission.
    // If its denied, it's a permanent block until the
    // user changes the permission
    if (Notification.permission === 'denied') {
      console.warn('The user has blocked notifications.');
      return;
    }

    // Check if push messaging is supported
    if (!('PushManager' in window)) {
      console.warn('Push messaging isn\'t supported.');
      return;
    }

    if(!authenticated){
      return;
    }

    // Check for notification permission and service worker support
    if ('Notification' in window && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          // Check existing subscription
          registration.pushManager.getSubscription()
            .then((subscription) => {
              if (subscription) {
                console.log('Already subscribed:', subscription);
              } else {
                requestNotificationPermission(registration);
              }
            })
            .catch((error) => {
              console.error('Error checking subscription:', error);
            });
        })
        .catch((error) => {
          console.error('Service Worker not ready:', error);
        });
    }
  }, []);

  // Handle expiration and logout
  useEffect(() => {
    if (expiration) {
      const expirationTime = new Date(expiration).getTime();
      const currentTime = Date.now();

      if (expirationTime - currentTime <= 0) {
        handleLogout();
        return;
      }

      const timeRemaining = expirationTime - currentTime;
      const refreshTimer = setTimeout(() => {
        window.location.reload(); // Refresh the page
      }, timeRemaining);

      return () => clearTimeout(refreshTimer);
    }
  }, [expiration, handleLogout]);

  useEffect(()=>{
    if(userRoles.length<=0){
      setSidebarOpen(false);
    }
  },[userRoles])

  if (loading) {
    return <LoadingIndicator message="Loading..." />;
  }

  return (
    <div className="app">
      {!location.pathname.startsWith('/error') && !location.pathname.startsWith('/unauthorized') && authenticated && userRoles.length>0 && (
        <div className="app-side-box">
          <Header onLogout={handleLogout} />
        </div>
      )}
      <ScrollToTop />
      <div className="app-body" 
        style={{ 
          marginLeft: sidebarOpen ? '280px' : '60px',
          transition: 'margin-left 0.3s ease',
          width: 'calc(100% - ' + (sidebarOpen ? '280px' : '60px') + ')',
          padding: '15px 15px'
        }}>
        <Routes>
          <Route path="/" element={<Navigate to="/tickets/All Tickets" />} />
          <Route path="*" element={<Navigate to="/not-found" />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/forum/*" element={<ForumFlowRoute />} />
            <Route
            path="/login"
            element={<Login authenticated={authenticated} location={location} />}
          />
          <Route path="/oauth2/redirect" element={
            <OAuth2RedirectHandler onLogin={handleLogin} />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="/unauthorized"
            element={
            <PrivateRoute
            element={<UnauthorizedPage userRoles={userRoles}/>}
            authenticated={authenticated}
            roles={[]}
            userRoles={userRoles}/>}
          />
          <Route
            path="/dashboard/bo"
            element={
            <PrivateRoute
            element={<DashboardBO  />}
            authenticated={authenticated}
            roles={['ADMIN','BOMANAGER']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/extract-text"
            element={
            <PrivateRoute
            element={<OCRUploadPage  />}
            authenticated={authenticated}
            roles={['USER']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/company-domains"
            element={
            <PrivateRoute
            element={<CompanyDomainsAdminPage  />}
            authenticated={authenticated}
            roles={['ADMIN']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/service-flows/*"
            element={
            <PrivateRoute
            element={<ServiceFlowRoutes />}
            authenticated={authenticated}
            roles={['BOMANAGER', 'FOMANAGER', 'ADMIN']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/holiday"
            element={
            <PrivateRoute
            element={<HolidayPage />}
            authenticated={authenticated}
            roles={['ADMIN']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/leave"
            element={
            <PrivateRoute
            element={<LeaveRequestPage />}
            authenticated={authenticated}
            roles={['BOUSER', 'BOMANAGER', 'ADMIN']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/tickets/:stage"
            element={
            <PrivateRoute
            element={<Home />}
            authenticated={authenticated}
            roles={['BOUSER', 'FOUSER', 'BOMANAGER', 'FOMANAGER', 'ADMIN']}
            userRoles={userRoles}/>}
          />
          <Route
            path="/create-ticket"
            element={
              <PrivateRoute
              element={ <TicketContainer title="create-ticket" /> }
              roles={['FOUSER', 'BOMANAGER', 'FOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/update-ticket/:ticketId"
            element={
              <PrivateRoute
              element={ <TicketContainer title="update-ticket" /> }
              roles={['FOUSER', 'BOMANAGER', 'FOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/view-ticket/:ticketId"
            element={
              <PrivateRoute
              element={ <TicketContainer title="view-ticket" /> }
              roles={['BOUSER', 'FOUSER', 'BOMANAGER', 'FOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/manage-user"
            element={
              <PrivateRoute
              element={ <ManageUserPage /> }
              roles={['ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/manage-user/add-user"
            element={
              <PrivateRoute
              element={ <AddUserPage /> }
              roles={['ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/manage-user/assign-role"
            element={
              <PrivateRoute
              element={ <AssignRolePage /> }
              roles={['ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/manage-user/update-information"
            element={
              <PrivateRoute
              element={ <UpdateUserInformationPage /> }
              roles={['ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/performance-report"
            element={
              <PrivateRoute
              element={ <Dashboard  /> }
              roles={['BOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/urgent-ticket-report"
            element={
              <PrivateRoute
              element={ <UrgentTicketRaiseReport  /> }
              roles={['FOMANAGER', 'BOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/manage-leave"
            element={
              <PrivateRoute
              element={ <ManageLeavePage  /> }
              roles={['BOMANAGER', 'ADMIN']}
              authenticated={authenticated}
              userRoles={userRoles}
              />
            }
          />
          <Route
            path="/assignment-preview"
            element={
              <PrivateRoute
                element={<AssignmentPreviewPage />}
                authenticated={authenticated}
                roles={['ADMIN', 'BOMANAGER']}
                userRoles={userRoles}
              />
            }
          />

          <Route
            path="/assignment-preview/manage-assign-ticket/:ruleCategory"
            element={
              <PrivateRoute
                element={<ManageAssignments />}
                authenticated={authenticated}
                roles={['ADMIN', 'BOMANAGER']}
                userRoles={userRoles}
              />
            }
          />
        </Routes>
      </div>
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} newestOnTop={false} closeOnClick draggable pauseOnHover />
    </div>
  );
};

// Wrap the App component with AuthProvider
const WrappedApp = () => (
  <AuthProvider>
    <WebSocketProvider>
      <SidebarProvider>
        <App />
      </SidebarProvider>
    </WebSocketProvider>
  </AuthProvider>
);

export default WrappedApp;
